<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import AutomationTabs from '../AutomationTabs.vue';
import CrmAutomationRow from './CrmAutomationRow.vue';
import CrmAutomationForm from './CrmAutomationForm.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const formRef = ref(null);
const editing = ref(null);
const showDeleteConfirmationPopup = ref(false);
const selectedAutomation = ref({});

const records = computed(() => getters['crmAutomations/getAutomations'].value);
const uiFlags = computed(() => getters['crmAutomations/getUIFlags'].value);
const funnels = computed(() => getters['crmDeals/getFunnels'].value);

onMounted(() => {
  store.dispatch('crmAutomations/fetch');
  store.dispatch('crmDeals/fetchFunnels');
  store.dispatch('teams/get');
  store.dispatch('agents/get');
  // O seletor de template das ações lê as caixas Cloud API daqui.
  store.dispatch('inboxes/get');
});

const openNew = () => {
  editing.value = null;
  formRef.value?.open();
};

const openEdit = automation => {
  editing.value = automation;
  formRef.value?.open();
};

const toggle = async automation => {
  await store.dispatch('crmAutomations/toggle', automation.id);
};

const openDeletePopup = automation => {
  selectedAutomation.value = automation;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  closeDeletePopup();
  await store.dispatch('crmAutomations/delete', selectedAutomation.value.id);
  useAlert(t('AUTOMATION.CRM.DELETED'));
};

const save = async payload => {
  try {
    if (editing.value) {
      await store.dispatch('crmAutomations/update', {
        id: editing.value.id,
        ...payload,
      });
    } else {
      await store.dispatch('crmAutomations/create', payload);
    }
    useAlert(t('AUTOMATION.CRM.SAVED'));
    formRef.value?.close();
  } catch (error) {
    useAlert(t('AUTOMATION.CRM.SAVE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION.CRM.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('AUTOMATION.CRM.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUTOMATION.CRM.HEADER')"
        :description="$t('AUTOMATION.CRM.DESCRIPTION')"
        feature-name="automation"
      >
        <template #actions>
          <Button
            :label="$t('AUTOMATION.CRM.HEADER_BTN_TXT')"
            size="sm"
            @click="openNew"
          />
        </template>
      </BaseSettingsHeader>
      <AutomationTabs />
    </template>
    <template #body>
      <div>
        <CrmAutomationRow
          v-for="automation in records"
          :key="automation.id"
          :automation="automation"
          :funnels="funnels"
          @edit="openEdit"
          @toggle="toggle"
          @delete="openDeletePopup"
        />
      </div>
    </template>

    <CrmAutomationForm
      ref="formRef"
      :automation="editing"
      :funnels="funnels"
      @save="save"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AUTOMATION.CRM.DELETE_CONFIRM')"
      :message="selectedAutomation.name"
      :confirm-text="$t('AUTOMATION.FORM.DELETE')"
      :reject-text="$t('AUTOMATION.CRM.FORM.CANCEL')"
    />
  </SettingsLayout>
</template>
